import {
  Box,
  Icon,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
  menuItemClasses,
  useTheme,
} from "@outschool/backpack";
import { EnrollmentMeetingPurchaseQueryQuery } from "@outschool/gql-frontend-generated";
import { faExclamationTriangle, faPlus } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import React, { useEffect, useRef, useState } from "react";

import * as Activity from "../../../../../shared/Activity";
import { ActivityEnrollQueryQuery } from "../../../../../shared/generated/graphql";
import { LearnerNameWarning } from "../../../learner/LearnerNameWarning";
import { EnrollmentWarnings } from "./EnrollmentWarnings";
import { LearnerItemType, defaultLearnerItem } from "./utils";

export * from "./utils";

type LearnerItemCheckboxesProps = {
  learnerItems: LearnerItemType[];
  onLearnerItemsUpdate(learnerItems: LearnerItemType[]): void;
  disabled: boolean;
  ignoreStrictAgeRange: boolean;
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  clearErrors?: () => void;
  highlightErrors: boolean;
};

export default function LearnerItemDropdown({
  disabled,
  learnerItems,
  onLearnerItemsUpdate,
  activity,
  ignoreStrictAgeRange,
  clearErrors,
  highlightErrors,
}: LearnerItemCheckboxesProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\LearnerItemDropdown",
  );
  const theme = useTheme();

  const [showNewLearnerInput, setShowNewLearnerInput] = useState(false);
  const [newLearnerName, setNewLearnerName] = useState<string | null>(null);
  const [newLearnerAge, setNewLearnerAge] = useState<number>(0);
  const [selectedLearnerItem, setSelectedLearnerItem] =
    useState<LearnerItemType | null>(null);
  const newLearnerNameRef = useRef<HTMLInputElement>();
  const ADD_LEARNER_KEY = "ADD_LEARNER";

  const createLearnerItemFromInput = (name: $TSFixMe, age: $TSFixMe) => {
    const addedLearner = defaultLearnerItem();
    addedLearner.learner.name = name;
    addedLearner.learner.age = age;
    return addedLearner;
  };

  const handleNewLearnerUpdate = (name: string | null, age: number) => {
    clearErrors?.();
    setNewLearnerName(name);
    setNewLearnerAge(age);
    const addedLearner = createLearnerItemFromInput(name, age);
    const newLearnerItems = learnerItems.filter(li => li.learner.uid);
    newLearnerItems.push(addedLearner);
    setSelectedLearnerItem(addedLearner);
    onLearnerItemsUpdate(newLearnerItems);
  };

  useEffect(() => {
    const initiallyCheckedLearnerItem = learnerItems.find(li => li.checked);
    if (initiallyCheckedLearnerItem) {
      setSelectedLearnerItem(initiallyCheckedLearnerItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectChange = (learnerKey: string) => {
    clearErrors?.();

    const updatedLearners: LearnerItemType[] = [];

    learnerItems.forEach(li => {
      // Filter out new learners and check any existing learners if they've been selected
      if (li.learner.uid) {
        const checked = li.key === learnerKey;
        const updatedLearner = { ...li, checked };
        updatedLearners.push(updatedLearner);
      }
    });

    if (learnerKey === ADD_LEARNER_KEY) {
      const newLearner = createLearnerItemFromInput(
        newLearnerName,
        newLearnerAge,
      );
      updatedLearners.push(newLearner);
      setShowNewLearnerInput(true);
    } else {
      setShowNewLearnerInput(false);
    }

    const selectedLearner = updatedLearners.find(li => li.checked);
    if (selectedLearner) {
      setSelectedLearnerItem(selectedLearner);
    }
    onLearnerItemsUpdate(updatedLearners);
  };

  const getLearnerSelectDefault = () => {
    const checkedLearnerItem = learnerItems.find(li => li.checked);
    return checkedLearnerItem ? checkedLearnerItem.key : "default";
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          marginRight: "0.5em",
          marginTop: "0.5em",
          marginBottom: "0.5em",
        }}
      >
        {t("Learner")}
      </Typography>
      <Select
        selectSx={{
          borderRadius: 10,
          width: "100%",
        }}
        sx={(theme: Theme) => ({
          width: "100%",
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderColor: theme.colors.gray200,
            },
        })}
        disabled={disabled}
        defaultValue={getLearnerSelectDefault()}
        onChange={({ target: { value } }) => {
          if (value) {
            handleSelectChange(value);
          }
        }}
        helperText={
          !highlightErrors
            ? null
            : !learnerItems.some(li => li.checked)
            ? t("Please select a learner")
            : learnerItems.length === 0 ||
              !learnerItems.some(li => !!li.learner.name && !!li.learner.age)
            ? t("Please add a Learner")
            : null
        }
        error={highlightErrors}
      >
        <MenuItem
          key="default"
          value="default"
          disabled
          sx={{
            [`&.${menuItemClasses.disabled}`]: {
              opacity: 0.7,
            },
          }}
        >
          {t("Select Learner")}
        </MenuItem>
        {learnerItems
          .filter(li => !!li.learner.uid && !!li.learner.age)
          .map(li => {
            const {
              learner,
              disabled,
              alreadyEnrolled,
              prepay,
              alreadyWaitlisted,
              pendingEnrollment,
              hasOverlappingPendingEnrollmentMeetingPurchase,
            } = li;

            const outsideStrictAgeRange = Activity.learnerOutsideStrictAgeRange(
              activity,
              ignoreStrictAgeRange,
              learner.age ?? 0,
            );
            const shouldBlockToddler = Activity.toddlerIsNotAllowed(
              activity,
              learner.age ?? 0,
            );

            const finalDisabled =
              (alreadyEnrolled && !prepay) ||
              outsideStrictAgeRange ||
              shouldBlockToddler ||
              disabled;

            let warningMessage = null;
            if (alreadyEnrolled) {
              warningMessage = t(
                "This learner is already enrolled in this class.",
              );
            } else if (pendingEnrollment) {
              warningMessage = hasOverlappingPendingEnrollmentMeetingPurchase
                ? t(
                    "This learner is has an overlapping pending enrollment in this class.",
                  )
                : t("This learner is has a pending enrollment in this class.");
            } else if (alreadyWaitlisted) {
              warningMessage = t(
                "This learner is already waitlisted in this class.",
              );
            } else if (outsideStrictAgeRange) {
              warningMessage = t(
                "This learner does not meet the strict age range for this class.",
              );
            } else if (shouldBlockToddler) {
              warningMessage = t(
                "This learner can only enroll in Together Time classes for ages 1-2.",
              );
            }

            return (
              <MenuItem
                key={li.key}
                value={li.key}
                disabled={finalDisabled}
                sx={(theme: Theme) => ({
                  [`&.${menuItemClasses.selected}`]: {
                    backgroundColor: `${theme.palette.primary[50]} !important`,
                  },
                  [`&.${menuItemClasses.selected}:hover`]: {
                    backgroundColor: `${theme.palette.primary[100]} !important`,
                  },
                })}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    gap: "0.75rem",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: "normal",
                    }}
                  >
                    {`${learner.name}, age ${learner.age}`}
                  </Typography>
                  {!!warningMessage && (
                    <Box
                      sx={{
                        marginBottom: "0.25em",
                        marginTop: "0.25em",
                        display: "flex",
                        flex: 2,
                        minWidth: 0,
                        whiteSpace: "normal",
                      }}
                    >
                      <Icon
                        icon={faExclamationTriangle}
                        sx={{
                          marginTop: "0.25em",
                          verticalAlign: "baseline",
                          marginRight: "0.25em",
                        }}
                        fixedWidth
                      />
                      <Typography
                        sx={{
                          flex: 2,
                          minWidth: 0,
                          whiteSpace: "normal",
                        }}
                      >
                        {warningMessage}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        <MenuItem key={ADD_LEARNER_KEY} value={ADD_LEARNER_KEY}>
          <Typography
            variant="inherit"
            sx={{
              fontWeight: "fontWeightBold",
              color: theme.palette.primary.main,
            }}
          >
            <Icon icon={faPlus} /> {t("New Learner")}
          </Typography>
        </MenuItem>
      </Select>

      {showNewLearnerInput && (
        <Box sx={{ display: "flex", paddingTop: "0.25em" }}>
          <TextField
            value={newLearnerName}
            inputRef={newLearnerNameRef}
            onChange={e =>
              handleNewLearnerUpdate(e.target.value, newLearnerAge)
            }
            sx={{ marginRight: "1em", width: "60%" }}
            placeholder={t("Learner name")}
          ></TextField>
          <Select
            selectSx={{ borderRadius: 35 }}
            value={newLearnerAge}
            sx={{ width: "20%", minWidth: "4em" }}
            onChange={({ target: { value } }) => {
              if (typeof value === "number") {
                handleNewLearnerUpdate(newLearnerName, value);
              }
            }}
          >
            <MenuItem key={0} value={0} disabled>
              {t("Age")}
            </MenuItem>
            {Activity.getAgeRangeOptions(activity, ignoreStrictAgeRange).map(
              age => (
                <MenuItem key={age} value={age}>
                  {age}
                </MenuItem>
              ),
            )}
          </Select>
        </Box>
      )}

      {/* Warnings */}
      <LearnerNameWarning name={newLearnerNameRef.current?.value || ""} />
      {selectedLearnerItem && (
        <EnrollmentWarnings
          ignoreStrictAgeRange={ignoreStrictAgeRange}
          learner={selectedLearnerItem?.learner}
          conflictingEnrollment={selectedLearnerItem.conflictingEnrollment}
          otherSectionEnrollment={selectedLearnerItem.otherSectionEnrollment}
          activity={activity}
        />
      )}
    </>
  );
}
